import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import '@/App.css';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import PaymentSuccess from './pages/PaymentSuccess';
import PhoneAppointment from './pages/PhoneAppointment';
import Contact from './pages/Contact';
import Profile from './pages/Profile';
import CatalogoProtesis from './pages/CatalogoProtesis';
import Chatbot from './components/Chatbot';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminAppointments from './pages/admin/AdminAppointments';
import AdminContactRequests from './pages/admin/AdminContactRequests';
import AdminQuotes from './pages/admin/AdminQuotes';
import AdminShipping from './pages/admin/AdminShipping';
import AdminNotices from './pages/admin/AdminNotices';
import AdminNewsletter from './pages/admin/AdminNewsletter';
import AdminGallery from './pages/admin/AdminGallery';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminLoyalty from './pages/admin/AdminLoyalty';
import AdminAbandonedCarts from './pages/admin/AdminAbandonedCarts';
import ResetPassword from './pages/ResetPassword';
import ActivateAccount from './pages/ActivateAccount';
import PublicTracking from './pages/PublicTracking';
import TermsConditions from './pages/TermsConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiesPolicy from './pages/CookiesPolicy';
import ShippingInfo from './pages/ShippingInfo';

function App() {
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const processAuth = async () => {
      // Check for session_id in URL fragment (Emergent Auth)
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        await processEmergentSession(sessionId);
        // Clean URL
        window.history.replaceState(null, '', window.location.pathname);
        setLoading(false);
        return;
      }

      // Check for existing token
      const token = localStorage.getItem('token');
      if (token) {
        await fetchUser(token);
      }
      setLoading(false);
    };

    const processEmergentSession = async (sessionId) => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/auth/emergent/session`, {
          method: 'POST',
          headers: { 'X-Session-ID': sessionId }
        });

        if (response.ok) {
          const data = await response.json();
          // Store session_token as regular token
          localStorage.setItem('token', data.session_token);
          setUser(data.user);
        }
      } catch (error) {
        console.error('Auth error:', error);
      }
    };

    const fetchUser = async (token) => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };

    processAuth();
  }, []);

  // Listen for global cart-updated events (fired by the chatbot when it adds
  // items via the ADD_CART tag). Re-fetches the cart from backend so the count
  // bubble in the navbar reflects reality without having to reload the page.
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const refreshCart = async () => {
      try {
        const sid = localStorage.getItem('sessionId');
        if (!sid) return;
        const res = await fetch(`${BACKEND_URL}/api/cart/${sid}`);
        if (res.ok) {
          const data = await res.json();
          const total = (data.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
          setCartCount(total);
        }
      } catch (e) {
        console.error('cart refresh error:', e);
      }
    };
    window.addEventListener('cart-updated', refreshCart);
    return () => window.removeEventListener('cart-updated', refreshCart);
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/" element={<Home user={user} setUser={setUser} cartCount={cartCount} setCartCount={setCartCount} />} />
          <Route path="/seguimiento/:orderId" element={<PublicTracking />} />
          <Route path="/productos" element={<Products user={user} setUser={setUser} cartCount={cartCount} setCartCount={setCartCount} />} />
          <Route path="/catalogo-protesis" element={<CatalogoProtesis user={user} cartCount={cartCount} />} />
          <Route path="/producto/:id" element={<ProductDetail user={user} setUser={setUser} cartCount={cartCount} setCartCount={setCartCount} />} />
          <Route path="/carrito" element={<Cart user={user} />} />
          <Route path="/checkout" element={<Checkout user={user} setUser={setUser} />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/activar-cuenta" element={<ActivateAccount />} />
          <Route path="/condiciones" element={<TermsConditions />} />
          <Route path="/privacidad" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          <Route path="/envios" element={<ShippingInfo />} />
          <Route path="/cita-telefonica" element={<PhoneAppointment user={user} setUser={setUser} cartCount={cartCount} setCartCount={setCartCount} />} />
          <Route path="/contacto-personalizado" element={<Contact user={user} />} />
          <Route path="/perfil" element={user ? <Profile user={user} setUser={setUser} cartCount={cartCount} setCartCount={setCartCount} /> : <Navigate to="/" />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin setUser={setUser} />} />
          <Route path="/admin" element={<AdminDashboard user={user} setUser={setUser} />} />
          <Route path="/admin/products" element={<AdminProducts user={user} setUser={setUser} />} />
          <Route path="/admin/orders" element={<AdminOrders user={user} setUser={setUser} />} />
          <Route path="/admin/customers" element={<AdminCustomers user={user} setUser={setUser} />} />
          <Route path="/admin/abandoned-carts" element={<AdminAbandonedCarts user={user} />} />
          <Route path="/admin/appointments" element={<AdminAppointments user={user} setUser={setUser} />} />
          <Route path="/admin/contact-requests" element={<AdminContactRequests user={user} setUser={setUser} />} />
          <Route path="/admin/quotes" element={<AdminQuotes user={user} setUser={setUser} />} />
          <Route path="/admin/shipping" element={<AdminShipping user={user} setUser={setUser} />} />
          <Route path="/admin/notices" element={<AdminNotices user={user} setUser={setUser} />} />
          <Route path="/admin/newsletter" element={<AdminNewsletter user={user} setUser={setUser} />} />
          <Route path="/admin/gallery" element={<AdminGallery user={user} setUser={setUser} />} />
          <Route path="/admin/coupons" element={<AdminCoupons user={user} setUser={setUser} />} />
          <Route path="/admin/loyalty" element={<AdminLoyalty user={user} setUser={setUser} />} />
        </Routes>
        <Chatbot />
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;